import React, { useState, useEffect, useCallback } from 'react';
import { useNotification } from '../context/NotificationContext';
import { useAuth } from '../context/AuthContext';
import { Link } from 'react-router-dom';
import LoadingSpinner from '../components/LoadingSpinner';
import ErrorAlert from '../components/ErrorAlert';
import { TrashIcon, EnvelopeOpenIcon, BellSlashIcon } from '@heroicons/react/24/outline';

/**
 * Page for viewing and managing all notifications
 */
function NotificationsPage() {
  const { 
    notifications, 
    loading, 
    error, 
    fetchNotifications, 
    markAsRead, 
    markAllAsRead,
    deleteNotification,
    deleteAllNotifications,
    showNotification
  } = useNotification();
  // User context is maintained for future authorization checks
  // eslint-disable-next-line no-unused-vars
  const { user } = useAuth();
  const [selectedFilter, setSelectedFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    // Fetch notifications when the component mounts
    fetchNotifications();
  }, [fetchNotifications]);

  // Filter notifications based on selected filter and search term
  const filteredNotifications = React.useMemo(() => {
    if (!Array.isArray(notifications)) return [];
    
    return notifications
      .filter(notification => {
        // Apply read/unread filter
        if (selectedFilter === 'unread') return !notification.read;
        if (selectedFilter === 'read') return notification.read;
        return true; // 'all' filter
      })
      .filter(notification => {
        // Apply search term filter
        if (!searchTerm) return true;
        
        const searchLower = searchTerm.toLowerCase();
        return (
          (notification.message && notification.message.toLowerCase().includes(searchLower)) ||
          (notification.title && notification.title.toLowerCase().includes(searchLower)) ||
          (notification.type && notification.type.toLowerCase().includes(searchLower))
        );
      })
      .sort((a, b) => {
        // Sort: Unread first, then by date (newest first)
        if (a.read !== b.read) return a.read ? 1 : -1;
        
        const dateA = new Date(a.createdAt || a.created_at);
        const dateB = new Date(b.createdAt || b.created_at);
        return dateB - dateA;
      });
  }, [notifications, selectedFilter, searchTerm]);

  const handleMarkAsRead = useCallback(async (id) => {
    try {
      await markAsRead(id);
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
  }, [markAsRead]);

  const handleMarkAllAsRead = useCallback(async () => {
    try {
      await markAllAsRead();
      showNotification('All notifications marked as read', 'success');
    } catch (error) {
      console.error('Error marking all notifications as read:', error);
      showNotification('Failed to mark notifications as read', 'error');
    }
  }, [markAllAsRead, showNotification]);
  
  const handleDeleteNotification = useCallback(async (id) => {
    try {
      await deleteNotification(id);
      showNotification('Notification deleted', 'success');
    } catch (error) {
      console.error('Error deleting notification:', error);
      showNotification('Failed to delete notification', 'error');
    }
  }, [deleteNotification, showNotification]);
  
  const handleDeleteAllNotifications = useCallback(async () => {
    if (window.confirm('Are you sure you want to delete all notifications? This action cannot be undone.')) {
      try {
        await deleteAllNotifications();
        showNotification('All notifications deleted', 'success');
      } catch (error) {
        console.error('Error deleting all notifications:', error);
        showNotification('Failed to delete all notifications', 'error');
      }
    }
  }, [deleteAllNotifications, showNotification]);

  // Format date to a more readable format
  const formatDate = useCallback((dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    
    // For today, show time only
    if (date.toDateString() === now.toDateString()) {
      return `Today at ${date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}`;
    }
    
    // For yesterday, show "Yesterday at time"
    const yesterday = new Date(now);
    yesterday.setDate(yesterday.getDate() - 1);
    if (date.toDateString() === yesterday.toDateString()) {
      return `Yesterday at ${date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}`;
    }
    
    // For this year, show Month Day at time
    if (date.getFullYear() === now.getFullYear()) {
      return `${date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} at ${date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}`;
    }
    
    // For other dates, show full date with year
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric'
    });
  }, []);

  // Get badge color based on notification type
  const getTypeBadgeColor = useCallback((type) => {
    switch (type.toLowerCase()) {
      case 'request':
        return 'bg-blue-100 text-blue-800';
      case 'listing':
        return 'bg-green-100 text-green-800';
      case 'system':
        return 'bg-purple-100 text-purple-800';
      case 'warning':
        return 'bg-red-100 text-red-800';
      case 'approval':
        return 'bg-yellow-100 text-yellow-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  }, []);

  if (loading) return <LoadingSpinner />;

  return (
    <div className="max-w-7xl mx-auto py-6 px-4 sm:px-6 lg:px-8">
      <div className="mb-6">
        <h1 className="text-2xl font-semibold text-gray-900">Notifications</h1>
        <p className="mt-1 text-sm text-gray-500">
          View and manage all your notifications in one place.
        </p>
      </div>

      {error && <ErrorAlert message={error} className="mb-6" />}

      {/* Filter and search controls */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-6 gap-4">
        <div className="inline-flex rounded-md shadow-sm">
          <button
            onClick={() => setSelectedFilter('all')}
            className={`px-4 py-2 text-sm font-medium rounded-l-md border ${
              selectedFilter === 'all' ? 'bg-primary-50 border-primary-500 text-primary-700' : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
            }`}
          >
            All
          </button>
          <button
            onClick={() => setSelectedFilter('unread')}
            className={`px-4 py-2 text-sm font-medium border-t border-b ${
              selectedFilter === 'unread' ? 'bg-primary-50 border-primary-500 text-primary-700' : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
            }`}
          >
            Unread
          </button>
          <button
            onClick={() => setSelectedFilter('read')}
            className={`px-4 py-2 text-sm font-medium rounded-r-md border ${
              selectedFilter === 'read' ? 'bg-primary-50 border-primary-500 text-primary-700' : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
            }`}
          >
            Read
          </button>
        </div>

        <div className="relative rounded-md shadow-sm w-full md:w-auto">
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Search notifications..."
            className="focus:ring-primary-500 focus:border-primary-500 block w-full pr-10 sm:text-sm border-gray-300 rounded-md"
          />
          <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
            <svg className="h-5 w-5 text-gray-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z" clipRule="evenodd" />
            </svg>
          </div>
        </div>
      </div>

      {/* Actions */}
      <div className="flex justify-end mb-6 space-x-4">
        <button
          onClick={handleMarkAllAsRead}
          className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
        >
          <EnvelopeOpenIcon className="h-5 w-5 mr-2" />
          Mark all as read
        </button>
        <button
          onClick={handleDeleteAllNotifications}
          className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
        >
          <TrashIcon className="h-5 w-5 mr-2" />
          Delete all
        </button>
      </div>

      {/* Notification list */}
      <div className="bg-white shadow overflow-hidden sm:rounded-md">
        {filteredNotifications.length > 0 ? (
          <ul className="divide-y divide-gray-200">
            {filteredNotifications.map((notification) => (
              <li key={notification.id}>
                <div className={`px-6 py-4 ${!notification.read ? 'bg-primary-50' : ''}`}>
                  <div className="flex items-center justify-between">
                    <div className="flex flex-col sm:flex-row sm:items-center">
                      <div className="flex items-center">
                        {!notification.read ? (
                          <span className="h-2 w-2 bg-primary-600 rounded-full mr-2" aria-hidden="true"></span>
                        ) : (
                          <span className="h-2 w-2 mr-2" aria-hidden="true"></span>
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between mb-1">
                          <h3 className="text-base font-medium text-gray-900 truncate">
                            {notification.title || "Notification"}
                          </h3>
                          <div className="ml-2 flex-shrink-0">
                            {notification.type && (
                              <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getTypeBadgeColor(notification.type)}`}>
                                {notification.type}
                              </span>
                            )}
                          </div>
                        </div>
                        <p className="text-sm text-gray-700">{notification.message}</p>
                        <p className="mt-1 text-xs text-gray-500">
                          {formatDate(notification.createdAt || notification.created_at)}
                        </p>
                      </div>
                    </div>
                    
                    <div className="ml-4 flex-shrink-0 flex">
                      {!notification.read && (
                        <button 
                          onClick={() => handleMarkAsRead(notification.id)}
                          className="mr-2 text-gray-400 hover:text-gray-500"
                          title="Mark as read"
                        >
                          <EnvelopeOpenIcon className="h-5 w-5" />
                        </button>
                      )}
                      <button 
                        onClick={() => handleDeleteNotification(notification.id)}
                        className="text-gray-400 hover:text-red-500"
                        title="Delete notification"
                      >
                        <TrashIcon className="h-5 w-5" />
                      </button>
                    </div>
                  </div>
                  
                  {/* Optional: Related action */}
                  {notification.actionUrl && (
                    <div className="mt-2">
                      <Link 
                        to={notification.actionUrl}
                        className="inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded-md shadow-sm text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                      >
                        {notification.actionText || "View Details"}
                      </Link>
                    </div>
                  )}
                </div>
              </li>
            ))}
          </ul>
        ) : (
          <div className="py-12 px-4 text-center">
            <BellSlashIcon className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-base font-medium text-gray-900">No notifications</h3>
            <p className="mt-1 text-sm text-gray-500">
              {searchTerm 
                ? "No notifications match your search criteria."
                : selectedFilter === 'unread' 
                  ? "You have no unread notifications."
                  : selectedFilter === 'read' 
                    ? "You have no read notifications."
                    : "You don't have any notifications yet."
              }
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

export default NotificationsPage;
